import FeatureFlags from 'src/config/feature-flags';
import type { blueprintLibraryCategories } from 'src/features/blueprints/common';
/* istanbul ignore file */
import type { LibraryApiTypeKind } from 'src/features/library-items/library-items.types';
import { getEnabledPlatforms } from 'src/hooks/useGetEnabledPlatforms';
import type {
  canAddLibraryTypes,
  categories,
  osRequirements as osReqs,
  publishers,
} from '../../common';
import { deviceTypes, platformTypes, visionRequirements } from '../../common';

type DeviceType = (typeof deviceTypes)[keyof typeof deviceTypes];
interface LibraryItemConfigOptions {
  getName: () => string;
  getDescription: () => string;
  getPublisher?: () => string;
  getOsRequirements?: () => Array<string>;
  name: string;
  blueprintLibraryName?: string;
  description: string;
  type: LibraryApiTypeKind;
  publisher: (typeof publishers)[keyof typeof publishers];
  devices?: Array<(typeof deviceTypes)[keyof typeof deviceTypes]>; // Make devices optional
  osRequirements: Array<(typeof osReqs)[keyof typeof osReqs]>;
  category: (typeof categories)[keyof typeof categories];
  blueprintCategory: (typeof blueprintLibraryCategories)[keyof typeof blueprintLibraryCategories];
  icon: string;
  categoryIcon?: string;
  canAdd: (typeof canAddLibraryTypes)[keyof typeof canAddLibraryTypes];
  identifier: string;
  routeType: string;
  getUrl: (o: { id: string }) => string;
  getAddUrl: (o: { id: string }) => string;
  isHidden?: (o: {
    account: any;
    environment: any;
    blueprint?: any;
  }) => boolean;
  isOldPath?: (o: { account: any; environment: any }) => boolean;
  singleBlueprintAllowed?: boolean;
  singleAssignmentMappingAllowed?: boolean;
  getDoNotManageUpdatesError?: () => string;
}
const visionFlag = 'ios-apple-vision-pro';
class LibraryItemConfig {
  getName: LibraryItemConfigOptions['getName'];
  getDescription: LibraryItemConfigOptions['getDescription'];
  getPublisher: LibraryItemConfigOptions['getPublisher'];
  getOsRequirements: LibraryItemConfigOptions['getOsRequirements'];
  name: LibraryItemConfigOptions['name'];
  blueprintLibraryName?: LibraryItemConfigOptions['blueprintLibraryName'];
  description: LibraryItemConfigOptions['description'];
  type: LibraryItemConfigOptions['type'];
  publisher: LibraryItemConfigOptions['publisher'];
  private _devices: LibraryItemConfigOptions['devices'];
  private _osRequirements: LibraryItemConfigOptions['osRequirements'];
  category: LibraryItemConfigOptions['category'];
  blueprintCategory: LibraryItemConfigOptions['blueprintCategory'];
  icon: LibraryItemConfigOptions['icon'];
  categoryIcon: LibraryItemConfigOptions['categoryIcon'];
  canAdd: LibraryItemConfigOptions['canAdd'];
  identifier: LibraryItemConfigOptions['identifier'];
  routeType: LibraryItemConfigOptions['routeType'];
  getUrl: LibraryItemConfigOptions['getUrl'];
  getAddUrl: LibraryItemConfigOptions['getAddUrl'];
  isHidden?: LibraryItemConfigOptions['isHidden'];
  isOldPath?: LibraryItemConfigOptions['isOldPath'];
  singleBlueprintAllowed?: LibraryItemConfigOptions['singleBlueprintAllowed'];
  singleAssignmentMappingAllowed?: LibraryItemConfigOptions['singleAssignmentMappingAllowed'];
  getDoNotManageUpdatesError?: LibraryItemConfigOptions['getDoNotManageUpdatesError'];

  constructor({
    getName,
    getDescription,
    getPublisher,
    getOsRequirements,
    name,
    blueprintLibraryName,
    description,
    type,
    publisher,
    devices = [], // Provide a default value for devices
    osRequirements,
    category,
    blueprintCategory,
    icon,
    categoryIcon,
    canAdd,
    identifier,
    routeType,
    getUrl,
    getAddUrl,
    isHidden,
    isOldPath,
    singleBlueprintAllowed,
    singleAssignmentMappingAllowed,
    getDoNotManageUpdatesError,
  }: LibraryItemConfigOptions) {
    this.getName = getName;
    this.getDescription = getDescription;
    this.getPublisher = getPublisher;
    this.getOsRequirements = getOsRequirements;
    this._osRequirements = osRequirements;
    this.name = name;
    this.blueprintLibraryName = blueprintLibraryName; // The name that appears for this item in blueprints.
    this.description = description;
    this.type = type;
    this.publisher = publisher;
    this._devices = devices;
    this.category = category;
    this.blueprintCategory = blueprintCategory; // Defines the category under the blueprint page for which the item falls under.
    this.icon = icon;
    this.categoryIcon = categoryIcon;
    this.canAdd = canAdd;
    this.identifier = identifier;
    this.routeType = routeType;
    this.getUrl = getUrl.bind(this);
    this.getAddUrl = getAddUrl;
    this.isHidden = isHidden || (() => false);
    this.isOldPath = isOldPath?.bind(this) || (() => false);
    this.singleBlueprintAllowed = singleBlueprintAllowed;
    this.singleAssignmentMappingAllowed = singleAssignmentMappingAllowed;
    this.getDoNotManageUpdatesError = getDoNotManageUpdatesError;
  }

  private _getDevices(): Array<string> {
    return this._devices.filter((device) => {
      switch (device as any) {
        case deviceTypes.VISION:
          return FeatureFlags.getFlag(visionFlag);
        case platformTypes.ANDROID:
          const { androidEnabled } = getEnabledPlatforms();
          return androidEnabled;
        case platformTypes.WINDOWS:
          const { windowsEnabled } = getEnabledPlatforms();
          return windowsEnabled;
      }

      return true;
    });
  }

  get devices(): Array<string> {
    return this._getDevices();
  }

  set devices(devices: Array<DeviceType>) {
    this._devices = devices;
  }

  private _getOsRequirements(): Array<string> {
    const visionRequirementsSet = new Set(visionRequirements);

    return this._osRequirements.filter((item) => {
      if (visionRequirementsSet.has(item)) {
        return FeatureFlags.getFlag(visionFlag);
      }
      return true;
    });
  }

  get osRequirements(): Array<string> {
    return this._getOsRequirements();
  }

  set osRequirements(osRequirements: Array<
    (typeof osReqs)[keyof typeof osReqs]
  >) {
    this._osRequirements = osRequirements;
  }
}

export default LibraryItemConfig;
